/* =============================================================
   CONFIRM — the app's one "are you sure?"

   Replaces the 52 SweetAlert confirms that were spread over 39 files.
   Each one drew SweetAlert's own frame: its orange warning ring, its
   bounce, its button colours typed in per call (and, where a page
   opted into customClass lms-popup, a confirm button popups.css
   forces to a saffron gradient). None of them looked like the house
   popup the view that opened them had just shown.

   Same shape as toast.js: a plain store plus a subscriber, no React
   here, so any handler can ask without being in a component. The
   options deliberately take SweetAlert's names -- title, text, html,
   icon, confirmButtonText, cancelButtonText, showDenyButton,
   denyButtonText, input, inputLabel, inputPlaceholder -- and the
   promise resolves to SweetAlert's result shape, { isConfirmed,
   isDenied, isDismissed, value }. So a call site changes
   `Swal.fire({ ... })` to `confirm({ ... })` and nothing after it.

   Colour options (confirmButtonColor, cancelButtonColor, iconColor,
   background, color, customClass, width, reverseButtons) are
   accepted and ignored: the frame decides colour, and a page cannot
   paint a confirm its own way again. The one thing a colour used to
   say -- "this is destructive" -- is kept: see toneOf below.
   ============================================================= */

let nextId = 1;
let current = null;
const queue = [];
const listeners = new Set();

const emit = () => {
  for (const listener of listeners) listener(current);
};

export const subscribe = (listener) => {
  listeners.add(listener);
  listener(current);
  return () => listeners.delete(listener);
};

/* An html body, as text. The six html confirms interpolate names --
   a batch, a student -- straight from the database into a string;
   rendering that as markup would put whatever those names contain on
   the page. Line breaks and list items survive as line breaks, which
   is all any of them used markup for. */
const htmlToText = (html) =>
  String(html ?? '')
    .replace(/<\s*br\s*\/?>/gi, '\n')
    .replace(/<\s*\/\s*(p|div|li|h[1-6]|tr)\s*>/gi, '\n')
    .replace(/<\s*li[^>]*>/gi, '• ')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\n{3,}/g, '\n\n')
    .trim();

const DANGER_COLOUR = /c0392b|b3202e|d33\b|dc3545|dd6b55|e74c3c|ef4444|dc2626|b91c1c|\bred\b|danger/i;
const DANGER_WORDS = /\b(delete|remove|discard|revoke|reject|cancel (the|this) |deactivate|archive permanently|clear all)\b/i;

/* Destructive or not. Said outright with `tone`, or read off what the
   call already said: a red confirm colour, or a verb like Delete on
   the button. Everything else is an ordinary confirmation. */
const toneOf = (options) => {
  if (options.tone === 'danger' || options.tone === 'default') return options.tone;
  if (DANGER_COLOUR.test(String(options.confirmButtonColor || ''))) return 'danger';
  if (DANGER_WORDS.test(String(options.confirmButtonText || ''))) return 'danger';
  return 'default';
};

const normalise = (options = {}) => ({
  title: String(options.title || 'Are you sure?'),
  text: options.html != null ? htmlToText(options.html) : String(options.text || ''),
  tone: toneOf(options),
  confirmText: String(options.confirmButtonText || 'Confirm'),
  cancelText: String(options.cancelButtonText || 'Cancel'),
  denyText: options.showDenyButton ? String(options.denyButtonText || 'No') : null,
  input: options.input
    ? {
        type: options.input === 'textarea' ? 'textarea' : 'text',
        label: String(options.inputLabel || ''),
        placeholder: String(options.inputPlaceholder || ''),
        value: String(options.inputValue || ''),
        required: Boolean(options.inputRequired),
      }
    : null,
});

const showNext = () => {
  current = queue.shift() || null;
  emit();
};

/**
 * Ask. Resolves to { isConfirmed, isDenied, isDismissed, value } --
 * SweetAlert's shape, so `if (result.isConfirmed)` and
 * `.then((r) => r.isConfirmed && ...)` keep working unchanged.
 * One dialog at a time: a second ask waits for the first to settle.
 */
export const confirm = (options = {}) =>
  new Promise((resolve) => {
    queue.push({ id: nextId++, ...normalise(options), resolve });
    if (!current) showNext();
  });

/** Called by the host. `outcome` is 'confirm' | 'deny' | 'dismiss'. */
export const settleConfirm = (outcome, value) => {
  if (!current) return;
  const { resolve, input } = current;
  current = null;
  resolve({
    isConfirmed: outcome === 'confirm',
    isDenied: outcome === 'deny',
    isDismissed: outcome === 'dismiss',
    value: outcome === 'confirm' ? (input ? value : true) : undefined,
  });
  showNext();
};

export default confirm;
